import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { Mail } from 'lucide-react';
import { useServiceNotificationMailers, type ServiceNotification } from '@/api/services';
import { useSendServiceNotificationEmail } from '@/api/services-mutations';

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

const muted = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' } as const;

/**
 * Send notification email — pick one of the customer's mailer configs and send the
 * technical notification through it. Receiver / CC default to the mailer config
 * but can be overridden before sending.
 */
export function ServiceNotificationSendDialog({
  serviceId,
  notification,
  open,
  onOpenChange,
}: {
  serviceId: number;
  notification: ServiceNotification | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const mailers = useServiceNotificationMailers(serviceId);
  const send = useSendServiceNotificationEmail(serviceId);
  const [mailerId, setMailerId] = useState<string>('');
  const [receivers, setReceivers] = useState('');
  const [cc, setCc] = useState('');

  const rows = mailers.data?.results ?? [];
  const selected = rows.find((m) => String(m.customer_mailer_id) === mailerId);

  // When a mailer is picked, seed the receiver/cc overrides from its config.
  useEffect(() => {
    if (selected) { setReceivers(selected.mailer_receiver ?? ''); setCc(selected.mailer_cc ?? ''); }
  }, [selected]);

  useEffect(() => {
    if (!open) { setMailerId(''); setReceivers(''); setCc(''); }
  }, [open]);

  if (!open || !notification) return null;

  const submit = () => {
    if (!mailerId) { toast.error('Please choose a mailer'); return; }
    send.mutate(
      {
        notificationId: notification.service_system_information_id,
        mailerId: Number(mailerId),
        mailer_receivers: receivers || undefined,
        mailer_cc: cc || undefined,
      },
      {
        onSuccess: () => { toast.success('Notification email sent'); onOpenChange(false); },
        onError: (e) => toast.error(errMsg(e)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Send notification email</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Alert tone="info" title={`Notification: ${notification.notification_name || `#${notification.service_system_information_id}`}`} />
            {mailers.isLoading ? (
              <span style={muted}>Loading mailers…</span>
            ) : rows.length === 0 ? (
              <EmptyState icon={<Mail size={22} />} title="No mailers" description="The customer has no mailer config to send email through." />
            ) : (
              <>
                <Field label="Mailer" required>
                  {() => (
                    <Select value={mailerId} onValueChange={setMailerId}>
                      <SelectTrigger><SelectValue placeholder="Choose a mailer…" /></SelectTrigger>
                      <SelectContent>{rows.map((m) => <SelectItem key={m.customer_mailer_id} value={String(m.customer_mailer_id)}>{m.customer_mailer_name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Recipient">
                  {(p) => <Input {...p} value={receivers} onChange={(e) => setReceivers(e.target.value)} placeholder="email@customer.com" />}
                </Field>
                <Field label="CC">
                  {(p) => <Input {...p} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@customer.com" />}
                </Field>
              </>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={send.isPending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!mailerId || send.isPending}>Send email</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
